import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  notFound,
  Outlet,
  redirect,
} from '@tanstack/react-router';

import { NotFoundPage } from '@pages/not-found';
import { isCampaignCategory } from '@shared/config/categories';
import { getViewerIdentity, hasAccountSession } from '@shared/lib/auth/viewer-identity';
import { authSessionApi } from '@shared/api';
import { tokenStore } from '@shared/lib/auth/token-store';

import { PageLoader } from '@shared/ui/page-loader';

import { GlobalSeo } from '@shared/ui/seo';
import { RouteLoadError } from '../ui/route-load-error';
import { getCampaignRedirect } from './campaign-redirect';

const rootRoute = createRootRoute({
  beforeLoad: async ({ location }) => {
    if (/^\/%40(?=[a-zA-Z0-9_-]+(?:\/|$))/i.test(location.pathname)) {
      return redirect({
        to: location.pathname.replace(/^\/%40/i, '/@'),
        search: location.search,
        hash: location.hash,
        replace: true,
      });
    }
    if (
      location.pathname === '/rankings' ||
      location.pathname === '/shop' ||
      location.pathname === '/play' ||
      location.pathname.startsWith('/play/')
    ) {
      // Resolve the persistent identity before game models and route guards mount.
      await authSessionApi.ensureGuest().catch(() => undefined);
    }
    if (location.pathname === '/play' || location.pathname.startsWith('/play/')) {
      const { loadCategoryPreferences } = await import('@entities/category');
      await loadCategoryPreferences(getViewerIdentity()?.login);
    }
    return undefined;
  },
  component: () => (
    <>
      <GlobalSeo />
      <Outlet />
    </>
  ),
  notFoundComponent: NotFoundPage,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@pages/root'), 'RootPage'),
});
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  beforeLoad: () => {
    if (hasAccountSession()) return redirect({ to: '/profile' });
    return undefined;
  },
  component: lazyRouteComponent(() => import('@pages/sign-in'), 'SignInPage'),
});
const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-up',
  beforeLoad: () => {
    if (hasAccountSession()) return redirect({ to: '/profile' });
    return undefined;
  },
  component: lazyRouteComponent(() => import('@pages/sign-up'), 'SignUpPage'),
});
const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email',
  component: lazyRouteComponent(() => import('@pages/verify-email/ui/verify-email-page'), 'VerifyEmailPage'),
});
const legacyVerifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth',
  component: lazyRouteComponent(() => import('@pages/verify-email/ui/verify-email-page'), 'VerifyEmailPage'),
});
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/reset',
  component: lazyRouteComponent(() => import('@pages/reset-password/ui/reset-password-page'), 'ResetPasswordPage'),
});
const oauthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/oauth/$provider',
  component: lazyRouteComponent(() => import('@features/auth/o-auth/ui/oauth-callback'), 'OAuthCallback'),
});
const completeSignUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/complete-sign-up',
  beforeLoad: () => {
    if (hasAccountSession()) return redirect({ to: '/profile' });
    if (!tokenStore.getTemporary()) return redirect({ to: '/sign-in' });
    return undefined;
  },
  component: lazyRouteComponent(() => import('@pages/complete-sign-up'), 'CompleteSignUpPage'),
});
const legacyHomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/home',
  beforeLoad: () => redirect({ to: '/', replace: true }),
});
const rankingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/rankings',
  validateSearch: (search: Record<string, unknown>) => ({
    profile:
      typeof search.profile === 'string' && /^@[a-zA-Z0-9_-]+$/.test(search.profile) ? search.profile : undefined,
  }),
  component: lazyRouteComponent(() => import('@pages/rankings'), 'RankingsPage'),
});
const playRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/play',
  component: lazyRouteComponent(() => import('@pages/play'), 'PlayPage'),
});
const campaignsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/play/campaigns',
  beforeLoad: async () => {
    const { readCategorySelection } = await import('@entities/category');
    const enabled = readCategorySelection(getViewerIdentity()?.login);
    const { getPublishedCampaign } = await import('@entities/campaign');
    const campaign = getPublishedCampaign();
    await campaign.load();
    if (campaign.loaded) await campaign.refresh(true);
    const login = getViewerIdentity()?.login;
    if (campaign.loaded && campaign.access(enabled, login) === 'campaign-complete') return redirect({ to: '/play' });
    return enabled.length === 1 && campaign.loaded && campaign.access(enabled, login, enabled[0]) === 'available'
      ? redirect({ to: '/play/campaigns/$categoryId', params: { categoryId: enabled[0]! } })
      : undefined;
  },
  component: lazyRouteComponent(() => import('@pages/campaigns'), 'CampaignsPage'),
});
const campaignCategoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/play/campaigns/$categoryId',
  beforeLoad: async ({ params }) => {
    const { readCategorySelection } = await import('@entities/category');
    const enabled = readCategorySelection(getViewerIdentity()?.login);
    if (!isCampaignCategory(params.categoryId) || !enabled.includes(params.categoryId)) {
      return redirect({ to: '/play/campaigns' });
    }
    const { getPublishedCampaign } = await import('@entities/campaign');
    const campaign = getPublishedCampaign();
    await campaign.load();
    if (campaign.loaded) await campaign.refresh(true);
    const destination = campaign.loaded
      ? getCampaignRedirect(campaign.access(enabled, getViewerIdentity()?.login, params.categoryId))
      : undefined;
    if (destination) return redirect(destination);
    return undefined;
  },
  component: lazyRouteComponent(() => import('@pages/campaigns'), 'CampaignCategoryPage'),
});
const campaignChapterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/play/campaigns/$categoryId/$chapterId',
  beforeLoad: async ({ params }) => {
    const { readCategorySelection } = await import('@entities/category');
    const login = getViewerIdentity()?.login;
    const enabled = readCategorySelection(login);
    if (!isCampaignCategory(params.categoryId) || !enabled.includes(params.categoryId)) {
      return redirect({ to: '/play/campaigns' });
    }
    const { getPublishedCampaign } = await import('@entities/campaign');
    const campaign = getPublishedCampaign();
    await campaign.load();
    if (!campaign.loaded) return undefined;
    await campaign.refresh(true);
    const access = campaign.access(enabled, login, params.categoryId, params.chapterId);
    const destination = getCampaignRedirect(access, params.categoryId, true);
    if (destination) return redirect(destination);
    return undefined;
  },
  component: lazyRouteComponent(() => import('@pages/campaigns'), 'CampaignChapterPage'),
});
const collectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/play/collections',
  validateSearch: (search: Record<string, unknown>) => ({
    packId: typeof search.packId === 'string' ? search.packId : undefined,
  }),
  component: lazyRouteComponent(() => import('@pages/collections'), 'CollectionsPage'),
});
const battlesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/play/battles',
  validateSearch: (search: Record<string, unknown>): { room?: string } => ({
    room:
      (typeof search.room === 'string' || typeof search.room === 'number') &&
      /^[a-zA-Z0-9_-]{4,64}$/.test(String(search.room))
        ? String(search.room)
        : undefined,
  }),
  component: lazyRouteComponent(() => import('@pages/battles'), 'BattlesPage'),
});
const legacyBattleInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/battles/$code',
  beforeLoad: ({ params }) =>
    redirect({
      to: '/play/battles',
      search: { room: /^[a-zA-Z0-9_-]{4,64}$/.test(params.code) ? params.code : undefined },
      replace: true,
    }),
});
const dailyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/play/daily',
  component: lazyRouteComponent(() => import('@pages/daily'), 'DailyPage'),
});
const dailyCategoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/play/daily/$categoryId',
  validateSearch: (search: Record<string, unknown>): { date?: string } => ({
    date: typeof search.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(search.date) ? search.date : undefined,
  }),
  beforeLoad: async ({ params }) => {
    const { readCategorySelection } = await import('@entities/category');
    if (
      !isCampaignCategory(params.categoryId) ||
      !readCategorySelection(getViewerIdentity()?.login).includes(params.categoryId)
    )
      return redirect({ to: '/play/daily' });
    return undefined;
  },
  component: lazyRouteComponent(() => import('@pages/daily'), 'DailyCategoryPage'),
});
const endlessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/play/endless',
  component: lazyRouteComponent(() => import('@pages/endless'), 'EndlessPage'),
});
const endlessGameRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/play/endless/game',
  beforeLoad: async () => {
    const { getSoloProgress } = await import('@entities/game-session');
    const { getGameResourceOwner } = await import('@shared/lib/auth/game-session');
    const { isCategoryEnabled, readCategorySelection } = await import('@entities/category');
    const store = getSoloProgress({ mode: 'endless' }, getGameResourceOwner() ?? '');
    await store.refresh(true);
    if (store.currentKind && !isCategoryEnabled(store.currentKind, readCategorySelection(getViewerIdentity()?.login)))
      return redirect({ to: '/play/endless' });
    return undefined;
  },
  component: lazyRouteComponent(() => import('@pages/endless'), 'EndlessGamePage'),
});
const endlessCategoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/play/endless/$categoryId',
  beforeLoad: async ({ params }) => {
    const { readCategorySelection } = await import('@entities/category');
    if (
      !isCampaignCategory(params.categoryId) ||
      !readCategorySelection(getViewerIdentity()?.login).includes(params.categoryId)
    )
      return redirect({ to: '/play/endless' });
    return undefined;
  },
  component: lazyRouteComponent(() => import('@pages/endless'), 'EndlessCategoryPage'),
});
const partyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/play/party',
  validateSearch: (search: Record<string, unknown>): { room?: string } => ({
    room:
      (typeof search.room === 'string' || typeof search.room === 'number') && /^\d{6}$/.test(String(search.room))
        ? String(search.room)
        : undefined,
  }),
  component: lazyRouteComponent(() => import('@pages/party'), 'PartyPage'),
});
const shopRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shop',
  validateSearch: (
    search: Record<string, unknown>,
  ): { category?: 'gameplay' | 'cosmetics' | 'currency' | 'other' } => ({
    category:
      search.category === 'gameplay' ||
      search.category === 'cosmetics' ||
      search.category === 'currency' ||
      search.category === 'other'
        ? search.category
        : undefined,
  }),
  component: lazyRouteComponent(() => import('@pages/shop'), 'ShopPage'),
});
const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile',
  beforeLoad: () => {
    if (!hasAccountSession()) return redirect({ to: '/sign-in' });
    const login = getViewerIdentity()?.login;
    return login ? redirect({ to: '/$userTag', params: { userTag: `@${login}` } }) : undefined;
  },
  component: lazyRouteComponent(() => import('@pages/profile'), 'ProfilePage'),
});
const userProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/$userTag',
  beforeLoad: ({ params }) => {
    if (!/^@[a-zA-Z0-9_-]+$/.test(params.userTag)) notFound({ throw: true });
  },
  component: lazyRouteComponent(() => import('@pages/profile'), 'ProfileByLoginPage'),
});
const profileBattlesRoute = createRoute({
  getParentRoute: () => userProfileRoute,
  path: '/battles',
  beforeLoad: ({ params }) => redirect({ to: '/$userTag', params: { userTag: params.userTag }, replace: true }),
});
const profileCollectionsRoute = createRoute({
  getParentRoute: () => userProfileRoute,
  path: '/collections',
  beforeLoad: ({ params }) => redirect({ to: '/$userTag', params: { userTag: params.userTag }, replace: true }),
});
const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: lazyRouteComponent(() => import('@pages/service-info'), 'TermsPage'),
});
const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: lazyRouteComponent(() => import('@pages/service-info'), 'PrivacyPage'),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  signInRoute,
  signUpRoute,
  verifyEmailRoute,
  legacyVerifyEmailRoute,
  resetPasswordRoute,
  oauthRoute,
  completeSignUpRoute,
  legacyHomeRoute,
  rankingsRoute,
  playRoute,
  campaignsRoute,
  campaignCategoryRoute,
  campaignChapterRoute,
  collectionsRoute,
  battlesRoute,
  legacyBattleInviteRoute,
  dailyRoute,
  dailyCategoryRoute,
  endlessRoute,
  endlessGameRoute,
  endlessCategoryRoute,
  partyRoute,
  shopRoute,
  profileRoute,
  userProfileRoute.addChildren([profileBattlesRoute, profileCollectionsRoute]),
  termsRoute,
  privacyRoute,
]);

export const router = createRouter({
  routeTree,
  pathParamsAllowedCharacters: ['@'],
  scrollRestoration: true,
  defaultPreload: 'intent',
  defaultPendingComponent: PageLoader,
  defaultErrorComponent: RouteLoadError,
  defaultPendingMs: 0,
  defaultPendingMinMs: 200,
  defaultPreloadStaleTime: 30000,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
